import React, { FunctionComponent, Suspense } from 'react';
import { useRelayStore } from 'src/relay';
import { backofficeUrl, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import ApiKeyIconAccountMenu from '../../../shared/svg/ApiKeyIconAccountMenu';
import HelpIcon from '../../../shared/svg/HeadsetAccountMenu';
import SupportIcon from '../../../shared/svg/InfoIconAccountMenu';
import LogoutIcon from '../../../shared/svg/LogoutIconAccountMenu';
import PaymentsIcon from '../../../shared/svg/MoneyIconAccountMenu';
import PermissionsIcon from '../../../shared/svg/PermissionsIconAccountMenu';
import ProductUpdatesIcon from '../../../shared/svg/RocketIconAccountMenu';
import AddonsIcon from '../../../shared/svg/ServerIconAccountMenu';
import PlanIcon from '../../../shared/svg/SlidersIconAccountMenu';
import UserIcon from '../../../shared/svg/UserIconAccountMenu';
import {
    DropdownContent,
    DropdownDivider,
    DropdownFooter,
    DropdownFooterLink,
    DropdownItemBody,
    DropdownItemCaption,
    DropdownItemIcon,
    DropdownItemText,
    DropdownList,
    DropdownListItem,
    DropdownSkeleton,
    DropdownSkeletonItemConfig
} from '../dropdown';
import { Logout } from 'src/shared/logout';

export interface AccountDropdownProps {
    nameUser: string;
    isMenuHidden: boolean;
    onItemClick: () => void;
}

const AccountMenuIcon = styled(DropdownItemIcon).attrs({ $size: 'small' as const })`
    color: #455a64;
`;

const AccountMenuTitle = styled(DropdownItemText)`
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    color: inherit;
`;

const AccountMenuDescription = styled(DropdownItemCaption)`
    color: inherit;
    opacity: 0.78;
`;

const AccountMenuFooter = styled(DropdownFooter)`
    justify-content: center;
    gap: 10px;
    font-size: 12px;
    color: #455a64;
`;

const accountDropdownSkeletonSections: DropdownSkeletonItemConfig[][] = [
    [
        { textWidth: '52%', captionWidth: '76%' },
        { textWidth: '44%', captionWidth: '68%' },
        { textWidth: '44%', captionWidth: '68%' },
        { textWidth: '44%', captionWidth: '68%' },
        { textWidth: '44%', captionWidth: '68%' }
    ],
    [{ textWidth: '30%', captionWidth: '58%' }]
];

interface AccountDropdownItemProps {
    icon?: React.ReactNode;
    title: string;
    description?: string;
    href?: string;
    danger?: boolean;
    onClick?: () => void;
}

const AccountDropdownItem: FunctionComponent<AccountDropdownItemProps> = ({ icon, title, description, href, danger, onClick }) => {
    const content = (
        <>
            {icon && <AccountMenuIcon>{icon}</AccountMenuIcon>}
            <DropdownItemBody>
                <AccountMenuTitle>{title}</AccountMenuTitle>
                {description && <AccountMenuDescription>{description}</AccountMenuDescription>}
            </DropdownItemBody>
        </>
    );

    if (href) {
        return (
            <DropdownListItem
                as='a'
                href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                $danger={danger}
                onClick={onClick}
            >
                {content}
            </DropdownListItem>
        );
    }

    return (
        <DropdownListItem type='button' $danger={danger} onClick={onClick}>
            {content}
        </DropdownListItem>
    );
};

const AccountDropdownContent: FunctionComponent<AccountDropdownProps> = (props) => {
    const invalidateRelayStore = useRelayStore((state) => state.invalidateRelayStore);

    const { subscription, sellerMarketplace } = useSubscriptionData();
    const canDisplayTab = sellerMarketplace !== 'MICROSOFT_AZURE' || subscription;
    const closeAndRun = (action?: () => void) => () => {
        props.onItemClick();
        action?.();
    };

    return (
        <>
            <DropdownContent>
                <DropdownList>
                    {!props.isMenuHidden && (
                        <AccountDropdownItem
                            icon={<UserIcon />}
                            title={props.nameUser}
                            description={T._('Manage your account details', 'Admin')}
                            href={backofficeUrl('/User')}
                            onClick={closeAndRun()}
                        />
                    )}
                    {!props.isMenuHidden && canDisplayTab && (
                        <>
                            <AccountDropdownItem
                                icon={<PlanIcon />}
                                title={T._('Subscription', 'Admin')}
                                description={T._('Access your subscription details', 'Admin')}
                                href={backofficeUrl('/User/?tab=subscriptions')}
                                onClick={closeAndRun()}
                            />
                            <AccountDropdownItem
                                icon={<AddonsIcon />}
                                title={T._('Add-ons', 'Admin')}
                                description={T._('View and activate addons', 'Admin')}
                                href={backofficeUrl('/User/?tab=subscriptions#addons-title')}
                                onClick={closeAndRun()}
                            />
                            <AccountDropdownItem
                                icon={<PaymentsIcon />}
                                title={T._('Payments', 'Admin')}
                                description={T._('Manage your payment details', 'Admin')}
                                href={backofficeUrl('/User/?tab=payments')}
                                onClick={closeAndRun()}
                            />
                            <DropdownDivider />
                            <AccountDropdownItem
                                icon={<ApiKeyIconAccountMenu />}
                                title={T._('API Keys', 'Admin')}
                                description={T._('Manage your account API keys', 'Admin')}
                                href={backofficeUrl('/User/?tab=api_keys')}
                                onClick={closeAndRun()}
                            />
                            <AccountDropdownItem
                                icon={<PermissionsIcon />}
                                title={T._('Users and Permissions', 'Admin')}
                                description={T._('Create sub-users for your account', 'Admin')}
                                href={backofficeUrl('/User/?tab=permissions')}
                                onClick={closeAndRun()}
                            />
                            <DropdownDivider />
                        </>
                    )}
                    {!props.isMenuHidden && (
                        <AccountDropdownItem
                            icon={<ProductUpdatesIcon />}
                            title={T._('Product Updates', 'Admin')}
                            description={T._('Check out our latest product updates', 'Admin')}
                            href='https://headwayapp.co/zakeke-changelog'
                            onClick={closeAndRun()}
                        />
                    )}
                    {!props.isMenuHidden && (
                        <AccountDropdownItem
                            icon={<HelpIcon />}
                            title={T._('Get Help', 'Admin')}
                            description={T._('Send us a message to get support', 'Admin')}
                            href='https://zakeke.zendesk.com/hc/en-us/requests/new'
                            onClick={closeAndRun()}
                        />
                    )}
                    {!props.isMenuHidden && (
                        <AccountDropdownItem
                            icon={<SupportIcon />}
                            title={T._('Support Center', 'Admin')}
                            description={T._('Access our knowledge base', 'Admin')}
                            href='https://zakeke.zendesk.com/hc/en-us/sections/360003652420'
                            onClick={closeAndRun()}
                        />
                    )}
                    <DropdownDivider />
                    <AccountDropdownItem
                        icon={<LogoutIcon />}
                        title={T._('Log out', 'Admin')}
                        description={T._('Sign out of your account', 'Admin')}
                        danger
                        onClick={closeAndRun(() => Logout({ invalidateRelayStore }))}
                    />
                </DropdownList>
            </DropdownContent>
            <AccountMenuFooter>
                <DropdownFooterLink href='https://www.zakeke.com/privacy-policy-platform/#general-conditions' target='_blank' rel='noopener noreferrer'>
                    {T._('General Conditions', 'Admin')}
                </DropdownFooterLink>
                <span aria-hidden='true'>•</span>
                <DropdownFooterLink href='https://www.zakeke.com/privacy-policy-platform/#privacy-policy' target='_blank' rel='noopener noreferrer'>
                    {T._('Privacy Policy', 'Admin')}
                </DropdownFooterLink>
            </AccountMenuFooter>
        </>
    );
};

const AccountDropdown: FunctionComponent<AccountDropdownProps> = (props) => (
    <Suspense fallback={<DropdownSkeleton sections={accountDropdownSkeletonSections} footerWidths={['110px', '1px', '84px']} footerJustify='center' />}>
        <AccountDropdownContent {...props} />
    </Suspense>
);

export default AccountDropdown;
